import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { 
  Upload, FileText, Clock, AlertCircle, CheckCircle2, 
  Filter, Search, RefreshCw, Download, Mail
} from 'lucide-react';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

// Import Email Ingestion Page
import EmailIngestionPage from './EmailIngestionPage';

// API
import { uploadDocument, listDocuments } from '../lib/api';
import { 
  formatConfidence, getConfidenceClass, getConfidenceColor, 
  getPriorityClass, getStatusClass, formatRelativeTime, formatCurrency 
} from '../lib/utils';

function UploadZone() {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: uploadDocument,
    onSuccess: (data) => {
      toast.success(`${data.filename} uploaded successfully!`);
      queryClient.invalidateQueries(['documents']);
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        await uploadMutation.mutateAsync(file);
      }
    } finally {
      setUploading(false);
    }
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  return (
    <Card className="animate-in">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Upload className="w-5 h-5" />
          <span>Upload EOB Documents</span>
        </CardTitle>
        <CardDescription>
          Drag and drop PDF files or click to browse
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          {...getRootProps()}
          data-testid="eob-upload-dropzone"
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${isDragActive 
              ? 'border-primary bg-primary/5 scale-[1.02]' 
              : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              {uploading ? (
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
              ) : (
                <FileText className="w-8 h-8 text-primary" />
              )}
            </div>
            
            {uploading ? (
              <div>
                <p className="text-sm font-medium text-foreground">Uploading...</p>
                <p className="text-xs text-muted-foreground mt-1">Please wait</p>
              </div>
            ) : isDragActive ? (
              <div>
                <p className="text-sm font-medium text-primary">Drop files here</p>
                <p className="text-xs text-muted-foreground mt-1">Release to upload</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-foreground">
                  Drag & drop EOB PDFs here
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  or <Button variant="link" className="h-auto p-0 text-xs" data-testid="eob-upload-browse-button">browse files</Button>
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  Supports: PDF • Max size: 50MB per file
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickStats() {
  const { data: documents } = useQuery({
    queryKey: ['documents', { limit: 100 }],
    queryFn: () => listDocuments({ limit: 100 }),
  });

  const docs = documents?.documents || [];
  
  const stats = {
    total: docs.length,
    processing: docs.filter(d => d.processing_status === 'processing').length,
    complete: docs.filter(d => d.processing_status === 'complete').length,
    failed: docs.filter(d => d.processing_status === 'failed').length,
  };

  const avgConfidence = docs.length > 0
    ? docs
        .filter(d => d.classification_confidence)
        .reduce((sum, d) => sum + d.classification_confidence, 0) / docs.filter(d => d.classification_confidence).length
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Total Documents</CardDescription>
          <CardTitle className="text-3xl">{stats.total}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <FileText className="w-3 h-3" />
            <span>Uploaded</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Processing</CardDescription>
          <CardTitle className="text-3xl text-info">{stats.processing}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>In Progress</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Completed</CardDescription>
          <CardTitle className="text-3xl text-success">{stats.complete}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <CheckCircle2 className="w-3 h-3" />
            <span>Processed</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Avg Confidence</CardDescription>
          <CardTitle className="text-3xl">{formatConfidence(avgConfidence)}</CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={avgConfidence * 100} className="h-1" />
        </CardContent>
      </Card>
    </div>
  );
}

function DocumentsTable() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    classification: '',
    priority: '',
    search: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['documents', filters],
    queryFn: () => listDocuments({
      status: filters.status || undefined,
      classification: filters.classification || undefined,
      priority: filters.priority || undefined,
      q: filters.search || undefined,
      limit: 50,
    }),
    refetchInterval: 5000, // Refetch every 5s to show processing status
  });

  const documents = data?.documents || [];

  return (
    <Card className="animate-in">
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Processing Queue</CardTitle>
            <CardDescription>View and manage uploaded documents</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => refetch()}
            data-testid="queue-refresh-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3" data-testid="queue-filter-tabs">
          <div className="flex-1">
            <Input
              placeholder="Search documents..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full"
            />
          </div>
          
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Status</SelectItem>
              <SelectItem value="uploaded">Uploaded</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Priority</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Standard">Standard</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid="eob-processing-queue-table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Document</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Uploaded</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No documents found. Upload some EOB PDFs to get started.
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow
                    key={doc.id}
                    data-testid="queue-row"
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-xs">{doc.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {doc.classification_type ? (
                        <span className="text-sm text-foreground">{doc.classification_type}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.priority ? (
                        <Badge 
                          className={getPriorityClass(doc.priority)} 
                          data-testid="queue-priority-badge"
                        >
                          {doc.priority}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={getStatusClass(doc.processing_status)}
                        data-testid="queue-status-badge"
                      >
                        {doc.processing_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {doc.classification_confidence ? (
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-muted rounded-full h-1.5 w-16">
                            <div
                              className={`h-full rounded-full ${getConfidenceColor(doc.classification_confidence)}`}
                              style={{ width: `${doc.classification_confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono">{formatConfidence(doc.classification_confidence)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelativeTime(doc.upload_date)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('upload');

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div>
        <h1 className="text-4xl font-bold mb-2">Extract</h1>
        <p className="text-muted-foreground">Upload and process EOB documents with AI</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="upload" className="flex items-center">
            <Upload className="w-4 h-4 mr-2" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Email Ingestion
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6 space-y-6">
          <QuickStats />
          <UploadZone />
          <DocumentsTable />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <EmailIngestionPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
